/*
 * Wall-clock ⇄ UTC in an IANA zone, DST-correct, pure `Intl` (no date
 * library) — the ONE implementation for the whole admin UI: `ui-domain`
 * re-exports it (`wallClockToUtcIso`, scheduled email, day ranges), `ui-forms`
 * `DateTimeWidget` reads and writes through it, modules format with it.
 * Moved here from `ui-domain/src/lib/time.tsx` because `ui-forms` depends only
 * on `ui` (spec fb-launch-builder v3 §10 sb.5, R2 U7).
 */
// ---------------------------------------------------------------------------
// Wall-clock ⇄ UTC conversion (DST-correct)
//
// Shared home for the Intl-offset technique previously duplicated between
// admin-module-conversations' schedule-control (full wall-clock, two-pass +
// DST-gap rejection) and this file's own single-pass startOfDayUtc — see
// M-021. Both callers now go through `wallClockToUtcIso` below.
// ---------------------------------------------------------------------------
/**
 * Thrown by `wallClockToUtcIso` when the requested wall-clock date+time falls
 * inside a DST spring-forward gap (a time that does not exist in the zone).
 * The caller should surface this as a user-facing validation error rather than
 * letting it propagate as an unhandled exception.
 */
export class NonexistentWallClockError extends Error {
    constructor() {
        super('NONEXISTENT_WALLCLOCK');
        this.name = 'NonexistentWallClockError';
    }
}
/** Measure the UTC offset (in ms) that `zone` applies at a given epoch-ms instant. */
export function measureZoneOffsetMs(epochMs, zone) {
    const dtf = new Intl.DateTimeFormat('en-US', {
        timeZone: zone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
    });
    const parts = dtf.formatToParts(new Date(epochMs));
    const get = (t) => parts.find((p) => p.type === t)?.value ?? '0';
    const zhh = Number(get('hour'));
    // `hour12:false` can render local midnight as hour '24' on some engines (a
    // V8/ICU quirk). This is a DISPLAY quirk only: the year/month/day emitted
    // ALONGSIDE the '24' already identify that midnight's calendar date (the
    // formatter rendered the instant `<date> 00:00` but printed the hour as 24).
    // So normalise the hour 24 → 0 and KEEP the emitted date. Do NOT advance the
    // day: that would push the measured offset 24 h too large and resolve a
    // 00:00 schedule to the wrong day.
    const normalizedHour = zhh === 24 ? 0 : zhh;
    const zoned = Date.UTC(Number(get('year')), Number(get('month')) - 1, Number(get('day')), normalizedHour, Number(get('minute')), Number(get('second')));
    return zoned - epochMs;
}
/**
 * Resolve a wall-clock date+time to a UTC ISO instant AS IF those wall-clock
 * components are in `zone` — e.g. the admin's configured timezone, NOT the
 * browser's.
 *
 * TWO-PASS DST-CORRECT MEASUREMENT:
 *
 * The UTC offset for a DST-observing timezone can differ depending on WHICH
 * instant you measure it at. A single-pass approach samples the offset at `asUtc`
 * (the wall-clock components naïvely interpreted as UTC), but near a DST
 * spring-forward or fall-back boundary the offset at `asUtc` can differ from the
 * offset at the TRUE local instant by the DST delta (~1 h), producing a resolved
 * instant that is off by that delta.
 *
 * This implementation uses a two-pass correction:
 *   Pass 1 — measure the zone offset at `asUtc`, derive candidate `t1 = asUtc − offset1`.
 *   Pass 2 — re-measure the zone offset AT `t1`. If `offset2 !== offset1`, recompute
 *             `t2 = asUtc − offset2` (the DST-correct answer). Otherwise `t1` is returned.
 *
 * For fixed-offset zones (e.g. Europe/Moscow, Asia/Tokyo) offset1 === offset2
 * always, so there is zero behaviour change.
 *
 * DST-GAP REJECTION (round-trip validation):
 *
 * After computing the candidate `result`, the function verifies that the instant
 * actually renders back to the requested wall-clock components. Concretely:
 * `result + measureZoneOffsetMs(result)` re-derives the wall-clock as a UTC epoch;
 * if it differs from `asUtc` the requested time does not exist in the zone (it
 * falls inside a spring-forward gap) and `NonexistentWallClockError` is thrown.
 * The caller should catch this and show an appropriate user-facing message.
 *
 * For valid times the round-trip is always exact. For fixed-offset zones the
 * round-trip is trivially satisfied (no DST gaps exist).
 */
export function wallClockToUtcIso(y, month1, // 1-based month
day, hh, mm, zone) {
    // The wall-clock components interpreted as if they were UTC.
    const asUtc = Date.UTC(y, month1 - 1, day, hh, mm, 0, 0);
    // Pass 1 — measure offset at the naïve UTC probe instant.
    const offset1 = measureZoneOffsetMs(asUtc, zone);
    const t1 = asUtc - offset1;
    // Pass 2 — re-measure at the candidate result; correct if the offset differs.
    const offset2 = measureZoneOffsetMs(t1, zone);
    const result = offset2 !== offset1 ? asUtc - offset2 : t1;
    // Round-trip validation: re-render `result` back to its wall-clock components
    // (via the offset at `result`) and check they match the requested wall-clock.
    // If they differ the requested time falls inside a DST spring-forward gap and
    // does not exist — reject rather than silently returning the wrong instant.
    const rendered = result + measureZoneOffsetMs(result, zone);
    if (rendered !== asUtc) {
        throw new NonexistentWallClockError();
    }
    return new Date(result).toISOString();
}
const pad2 = (n) => String(n).padStart(2, '0');
/**
 * ISO instant → wall-clock date and time in `zone` (inverse of
 * `wallClockToUtcIso`). `null` — not a date or an unknown zone.
 */
export function isoToZonedParts(iso, zone) {
    const ms = Date.parse(iso);
    if (Number.isNaN(ms))
        return null;
    let offset;
    try {
        offset = measureZoneOffsetMs(ms, zone);
    }
    catch {
        return null;
    }
    const wall = new Date(ms + offset);
    return {
        date: `${wall.getUTCFullYear()}-${pad2(wall.getUTCMonth() + 1)}-${pad2(wall.getUTCDate())}`,
        time: `${pad2(wall.getUTCHours())}:${pad2(wall.getUTCMinutes())}`,
    };
}
/**
 * `ZonedParts` → UTC ISO instant via `wallClockToUtcIso`. `null` — malformed
 * parts or a time inside a DST spring-forward gap (it does not exist).
 */
export function zonedPartsToIso(parts, zone) {
    const dm = /^(\d{4})-(\d{2})-(\d{2})$/.exec(parts.date);
    const tm = /^(\d{2}):(\d{2})$/.exec(parts.time);
    if (!dm || !tm)
        return null;
    try {
        return wallClockToUtcIso(Number(dm[1]), Number(dm[2]), Number(dm[3]), Number(tm[1]), Number(tm[2]), zone);
    }
    catch (err) {
        if (err instanceof NonexistentWallClockError || err instanceof RangeError)
            return null;
        throw err;
    }
}
//# sourceMappingURL=zoned-time.js.map